import type * as FavCache from '@finn-no/favorite-api-browser-client';
import { useRef } from 'react';
import { toast } from '@warp-ds/elements/toast';
import { i18n } from '@lingui/core';
import {
    AddFavoriteError,
    createFolder,
    CreateFolderError,
    markAsFavorite,
    Result,
} from '../ApiCallHandler';

type useCreateListProps = {
    api: FavCache.FavoriteBrowserCache;
    itemType: FavCache.ItemType;
    itemId: number;
    setErrorMessage: (errorMessage: string) => void;
    closeCallback: () => void;
};

const useCreateList = ({
    api,
    itemType,
    itemId,
    setErrorMessage,
    closeCallback,
}: useCreateListProps) => {
    const submittingRef = useRef(false);

    return async (folderName: string) => {
        // Just ignore additional submit events while processing the current request
        if (submittingRef.current) return;

        submittingRef.current = true;

        try {
            const createFolderResult: Result<number, CreateFolderError> =
                await createFolder({ api, folderName });
            if (!createFolderResult.ok) throw createFolderResult.error;

            const addFavoriteResult: Result<void, AddFavoriteError> =
                await markAsFavorite({
                    api,
                    folderId: createFolderResult.value,
                    itemId,
                    itemType,
                });
            if (!addFavoriteResult.ok) throw addFavoriteResult.error;
            toast(
                i18n.t({
                    id: 'favoriteAddedToast',
                    values: { folder: folderName },
                }),
                { type: 'success' },
            );
            closeCallback();
        } catch (e: unknown) {
            submittingRef.current = false;
            switch (true) {
                case e instanceof CreateFolderError:
                    console.error(e);
                    if (e.isDueToConflict) {
                        setErrorMessage(
                            i18n.t({
                                id: 'duplicateListError',
                            }),
                        );
                    } else {
                        setErrorMessage(
                            i18n.t({
                                id: 'createListError',
                            }),
                        );
                    }
                    break;
                case e instanceof AddFavoriteError:
                    console.error(e);
                    toast(
                        i18n.t({
                            id: 'favoriteAddedErrorToast',
                        }),
                        {
                            type: 'error',
                        },
                    );
                    closeCallback();
                    break;
                default:
                    console.error('Unknown error occurred', e);
            }
        }
    };
};

export default useCreateList;
